import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Languages, Search, Filter, Plus, Edit, Trash2, Check,
  X, ChevronDown, Globe, Flag, Clock, Settings,
  FileText, ExternalLink, AlertCircle, Monitor,
  Upload, Download, BookOpen, FileCheck, MessageSquare, CheckCircle
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import { useLanguage } from "../../modules/admin/hooks/useLanguage";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import LanguageModal from "./LanguageModal";
import {
  Language,
  LanguageDirectionEnum,
  TranslationEnableEnum,
  LanguageAutoTranslateEnum,
  LanguageTranslationMemoryEnum
} from "../../modules/shared/model/LanguageInterfaces";

const LanguageManager = () => {
  const dispatch = useAppDispatch();

  // Hooks
  const {
    languages,
    loading,
    error,
    totalCount,
    hasNextPage,
    currentPage,
    currentLimit,
    fetchLanguages,
    createNewLanguage,
    updateExistingLanguage,
    deleteExistingLanguage,
    clearSelected,
    clearErrorState,
    setPage,
    setLimit
  } = useLanguage();

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDirection, setSelectedDirection] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<Language | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    initial: "",
    direction: { id: LanguageDirectionEnum.ltr },
    isDefault: false,
    isTranslationEnable: true,
    isAutomaticSync: false,
    isNotify: false,
    autoTranslate: { id: LanguageAutoTranslateEnum.disable },
    translationMemory: { id: LanguageTranslationMemoryEnum.disable },
    categories: [] as Array<{ id: string }>
  });

  // Ref to prevent double API calls
  const isFetchingRef = useRef(false);

  // Calculate stats from languages data
  const stats = useMemo(() => {
    if (!languages) return { total: 0, active: 0, default: 0, rtl: 0 };

    const total = languages.length;
    const active = languages.filter(lang => lang?.status === "active").length;
    const defaultLang = languages.filter(lang => lang?.isDefault).length;
    const rtl = languages.filter(lang => Number(lang?.direction?.id) === LanguageDirectionEnum.rtl).length;

    return { total, active, default: defaultLang, rtl };
  }, [languages]);

  const fetchInitialData = async () => {
    // No API calls needed for static data
    // Directions, categories, and translation settings are handled by enums
  };

  const fetchLanguagesData = useCallback(async () => {
    // Prevent double API calls
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    try {
      await fetchLanguages({
        page: currentPage,
        limit: currentLimit,
        search: searchQuery,
        sortBy,
        sortOrder: sortOrder as "asc" | "desc",
        status: selectedStatus !== "all" ? selectedStatus : "",
        direction: selectedDirection !== "all" ? selectedDirection : "",
        isShowLoader: true
      });
    } finally {
      isFetchingRef.current = false;
    }
  }, [fetchLanguages, currentPage, currentLimit, searchQuery, sortBy, sortOrder, selectedStatus, selectedDirection]);

  // Fetch initial data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch languages when filters change
  useEffect(() => {
    fetchLanguagesData();
  }, [fetchLanguagesData]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1); // Reset to first page when searching
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case "type":
        setSelectedType(value);
        break;
      case "status":
        setSelectedStatus(value);
        break;
      case "direction":
        setSelectedDirection(value);
        break;
    }
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    // Batch state updates to prevent double API calls
    setLimit(newLimit);
    setPage(1);
  };

  const resetFormData = useCallback(() => {
    setFormData({
      name: "",
      initial: "",
      direction: { id: LanguageDirectionEnum.ltr },
      isDefault: false,
      isTranslationEnable: true,
      isAutomaticSync: false,
      isNotify: false,
      autoTranslate: { id: LanguageAutoTranslateEnum.disable },
      translationMemory: { id: LanguageTranslationMemoryEnum.disable },
      categories: []
    });
  }, []);

  const handleAddNew = useCallback(() => {
    setSuccessMessage(""); // Clear any existing success message
    resetFormData();
    setShowAddModal(true);
  }, [resetFormData]);

  const handleEdit = useCallback((language: Language) => {
    setSuccessMessage(""); // Clear any existing success message

    // Map the language data to form data
    const mappedData = {
      name: language.name || "",
      initial: language.initial || "",
      direction: { id: Number(language.direction?.id || language.direction) || LanguageDirectionEnum.ltr },
      isDefault: language.isDefault || false,
      isTranslationEnable: language.isTranslationEnable || true,
      isAutomaticSync: language.isAutomaticSync || false,
      isNotify: language.isNotify || false,
      autoTranslate: { id: Number(language.autoTranslate?.id || language.autoTranslate) || LanguageAutoTranslateEnum.disable },
      translationMemory: { id: Number(language.translationMemory?.id || language.translationMemory) || LanguageTranslationMemoryEnum.disable },
      categories: language.categories || []
    };
    setFormData(mappedData);
    setShowEditModal(language);
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this language?")) {
      try {
        await deleteExistingLanguage(id);
        // Refresh the current page data
        await fetchLanguagesData();
        setSuccessMessage("Language deleted successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
              } catch (error) {
        }
    }
  };

  const handleSubmit = useCallback(async (data: any, isEdit: boolean = false) => {
    try {
      // Convert enum values to proper format for API
      const processedData = {
        ...data,
        direction: { id: Number(data.direction?.id || data.direction) },
        autoTranslate: { id: Number(data.autoTranslate?.id || data.autoTranslate) },
        translationMemory: { id: Number(data.translationMemory?.id || data.translationMemory) },
        categories: data.categories?.map((cat: any) =>
          typeof cat === "object" ? cat : { id: cat }
        ) || []
      };

      if (isEdit && showEditModal) {
        await updateExistingLanguage(showEditModal.id, processedData);
        setSuccessMessage("Language updated successfully!");
      } else {
        await createNewLanguage(processedData);
        setSuccessMessage("Language created successfully!");
      }

      // Close modal and refresh data
      setShowAddModal(false);
      setShowEditModal(null);
      resetFormData();

      // Fetch latest languages to show in listing
      await fetchLanguagesData();

      // Also reset to first page to show the latest languages
      setPage(1);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
            } catch (error) {
        }
  }, [showEditModal, updateExistingLanguage, createNewLanguage, resetFormData, fetchLanguagesData, setPage]);

  const handleCloseModal = useCallback(() => {
    setSuccessMessage(""); // Clear any existing success message
    setShowAddModal(false);
    setShowEditModal(null);
    resetFormData();
  }, [resetFormData]);

  // Helper functions
  const getDirectionName = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case LanguageDirectionEnum.ltr:
        return "LTR";
      case LanguageDirectionEnum.rtl:
        return "RTL";
      default:
        return "Unknown";
    }
  };

  const getDirectionColor = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case LanguageDirectionEnum.rtl:
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  const getTranslationStatusColor = (enabled: boolean) => {
    return enabled ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";
  };

  // Language Card Component
  const LanguageCard = ({ language }: { language: Language | null | undefined }) => {
    // Early return if language is null or undefined
    if (!language) {
      return null;
    }

    return (
      <Card className="p-4 md:p-6" style={{ textAlign: "left" }}>
        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto">
            <div className="p-2 rounded-lg bg-purple-100">
              <Languages className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <h3 className="font-semibold text-lg">{language.name || "Unnamed Language"}</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={undefined} className={
                    language?.status === "active" ? "bg-green-100 text-green-700" :
                      "bg-yellow-100 text-yellow-700"
                  }>
                    {language?.status?.toUpperCase() || "ACTIVE"}
                  </Badge>
                  {language?.isDefault && (
                    <Badge variant={undefined} className="bg-blue-100 text-blue-700">Default</Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Globe className="h-4 w-4" />
                  {language.initial?.toUpperCase()}
                </div>
                <span className="hidden md:inline text-gray-400">|</span>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Last updated: {language.updatedAt ? new Date(language.updatedAt).toLocaleDateString() : "N/A"}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2 self-end md:self-start">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(language)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600"
              onClick={() => handleDelete(language.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Language Info */}
        <div className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Direction</span>
                <Badge variant={undefined} className={getDirectionColor(language.direction?.id)}>
                  {getDirectionName(language.direction?.id)}
                </Badge>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Translation</span>
                <Badge variant={undefined} className={getTranslationStatusColor(language?.isTranslationEnable)}>
                  {language?.isTranslationEnable ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        {language?.categories && language.categories.length > 0 && (
          <div className="mt-6">
            <h4 className="font-medium mb-3">Service Categories</h4>
            <div className="flex flex-wrap gap-2">
              {language.categories.map((category, index) => (
                <Badge key={index} variant="outline" className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {category.name || `Category ${index + 1}`}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Settings */}
        <div className="mt-6 pt-6 border-t">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-gray-500">Auto Translate</span>
              <p className="font-medium mt-1">
                {Number(language.autoTranslate?.id) === LanguageAutoTranslateEnum.enable ? "Enabled" : "Disabled"}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Translation Memory</span>
              <p className="font-medium mt-1">
                {Number(language.translationMemory?.id) === LanguageTranslationMemoryEnum.enable ? "Enabled" : "Disabled"}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Auto Sync</span>
              <p className="font-medium mt-1">
                {language?.isAutomaticSync ? "Yes" : "No"}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Notifications</span>
              <p className="font-medium mt-1">
                {language?.isNotify ? "Yes" : "No"}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 pt-6 border-t flex flex-col sm:flex-row justify-end gap-3">
          <Button
            variant="outline"
            className="flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button
            className="flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Upload className="h-4 w-4" />
            Import Translations
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="lg:pl-64 pt-16">
        <div className="p-4 md:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Language Manager</h1>
                <p className="text-gray-600 mt-1">Manage platform languages and translations</p>
              </div>
              <Button
                onClick={handleAddNew}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                Add Language
              </Button>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <p className="text-green-800 font-medium">{successMessage}</p>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Languages</p>
                    <p className="text-2xl font-semibold mt-1">{stats.total}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Languages className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Languages</p>
                    <p className="text-2xl font-semibold mt-1">{stats.active}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Globe className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Default Languages</p>
                    <p className="text-2xl font-semibold mt-1">{stats.default}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Flag className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">RTL Languages</p>
                    <p className="text-2xl font-semibold mt-1">{stats.rtl}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 flex-1">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search languages..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split("-");
                    setSortBy(field);
                    setSortOrder(order as "asc" | "desc");
                  }}
                  className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                >
                  <option value="createdAt-desc">Newest First</option>
                  <option value="createdAt-asc">Oldest First</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                </select>
              </div>

              {showFilters && (
                <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      className="w-full px-3 py-2 border rounded-lg"
                      value={selectedStatus}
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
                    <select
                      className="w-full px-3 py-2 border rounded-lg"
                      value={selectedDirection}
                      onChange={(e) => handleFilterChange("direction", e.target.value)}
                    >
                      <option value="all">All Directions</option>
                      <option value={LanguageDirectionEnum.ltr}>LTR</option>
                      <option value={LanguageDirectionEnum.rtl}>RTL</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      className="w-full px-3 py-2 border rounded-lg"
                      value={selectedType}
                      onChange={(e) => handleFilterChange("type", e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="default">Default</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>
              )}
            </Card>

            {/* Languages List */}
            <div className="grid grid-cols-1 gap-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading languages...</p>
                </div>
              ) : languages && languages.length > 0 ? (
                languages
                  .filter((language) => language != null)
                  .map((language) => (
                    <LanguageCard key={language?.id} language={language} />
                  ))
              ) : (
                <div className="text-center py-8">
                  <Languages className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No languages found</h3>
                  <p className="text-gray-600 mb-4">
                    {!loading && !error
                      ? "Get started by creating your first language."
                      : "Create your first language to get started."
                    }
                  </p>
                  <Button onClick={handleAddNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Language
                  </Button>
                </div>
              )}
            </div>

            {/* Pagination */}
            {languages && languages.length > 0 && totalCount > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={currentLimit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">
                    {(() => {
                      const page = currentPage || 1;
                      const limit = currentLimit || 10;
                      const startItem = ((page - 1) * limit) + 1;
                      const endItem = Math.min(page * limit, totalCount || 0);
                      return `${startItem}-${endItem} of ${totalCount || 0} results`;
                    })()}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange((currentPage || 1) - 1)}
                    disabled={(currentPage || 1) <= 1}
                  >
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const page = currentPage || 1;
                      const limit = currentLimit || 10;
                      const total = totalCount || 0;
                      const totalPages = Math.ceil(total / limit);
                      const maxVisiblePages = 5;
                      const pages = [];
                      
                      // Calculate start and end page numbers
                      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
                      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      // Adjust start page if we're near the end
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }
                      
                      // Add first page and ellipsis if needed
                      if (startPage > 1) {
                        pages.push(
                          <Button
                            key={1}
                            variant={page === 1 ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            className="w-8 h-8 p-0"
                          >
                            1
                          </Button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="ellipsis-start" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                      }
                      
                      // Add visible page numbers
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={page === i ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(i)}
                            className="w-8 h-8 p-0"
                          >
                            {i}
                          </Button>
                        );
                      }
                      
                      // Add ellipsis and last page if needed
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="ellipsis-end" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <Button
                            key={totalPages}
                            variant={page === totalPages ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        );
                      }
                      
                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange((currentPage || 1) + 1)}
                    disabled={!hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {(showAddModal || showEditModal) && (
        <LanguageModal
          language={showEditModal}
          isOpen={true}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
        />
      )}
    </div>
  );
};

export default LanguageManager;